import posthog from "posthog-js";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";

import { ResetIcon } from "components/atoms/Icon";
import { Paragraph } from "components/atoms/Typography";
import { useCustomPlanUpdate } from "hooks/CustomPlan/useCustomPlanUpdate";

import { buildingQueryKey } from "ApiLayer/Building/queryBuildingModel";
import { resetBMCRPQueries } from "hooks/invalidateBMCRPQueries";
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import { useRemodelBuildingModel } from "mutations/useRemodelBuildingModel";
import styles from "./EditCustomPlanButton.module.scss";

interface RemodelPlanButtonProps {
    buildingModelCarbonReductionPlan: BuildingModelCarbonReductionPlan;
}
function RemodelPlanButton({ buildingModelCarbonReductionPlan }: RemodelPlanButtonProps) {
    const { planView } = useParams();
    const buildingModelUid = useBuildingModelUid();

    const queryClient = useQueryClient();

    const { updateCustomPlanMeasures, isLoading: isLoadingCustomPlanUpdate } = useCustomPlanUpdate({
        buildingModelUid: buildingModelUid || "",
        buildingModelCarbonReductionPlanId: buildingModelCarbonReductionPlan.id,
    });

    const { isLoading: isLoadingRemodelBuildingModel, mutate: mutationRemodelBuildingModel } =
        useRemodelBuildingModel();

    const isLoading = isLoadingCustomPlanUpdate || isLoadingRemodelBuildingModel;

    const audettePlanSelected = buildingModelCarbonReductionPlan.planType !== "custom" || planView !== "custom";

    const onClick = () => {
        if (audettePlanSelected) {
            mutationRemodelBuildingModel([buildingModelUid], {
                onSuccess: async () => {
                    await queryClient.invalidateQueries({
                        queryKey: [...buildingQueryKey, buildingModelUid],
                    });

                    resetBMCRPQueries(buildingModelCarbonReductionPlan.id, queryClient);
                },
            });
        } else {
            updateCustomPlanMeasures("remodel", {
                onSuccess: () =>
                    queryClient.invalidateQueries({
                        queryKey: [...buildingQueryKey, buildingModelUid],
                    }),
            });
        }
        posthog.capture("Custom plan re-modelled.");
    };

    return (
        <button className={styles.addProjectButton} type="button" onClick={onClick} disabled={isLoading}>
            <ResetIcon height={12} />
            <Paragraph>{isLoading ? "Remodelling..." : "Remodel plan"}</Paragraph>
        </button>
    );
}

export default RemodelPlanButton;
